import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

declare global {
  interface Window {
    zE?: (...args: any[]) => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ZendeskService {

  private loaded = false;
  private authenticated = false;
  private readonly key = environment.zendeskKey;
  private locale = 'fr';

  private readyPromise: Promise<void>;
  private resolveReady: () => void;

  private authPromise: Promise<void>;
  private resolveAuth: () => void;

  private wantVisible = false;

  constructor(private readonly http: HttpClient) {
    this.readyPromise = new Promise(resolve => this.resolveReady = resolve);
    this.resetAuthPromise();
  }

  get isAvailable(): boolean {
    return !!this.key;
  }

  /** Load the Zendesk Messaging widget (launcher hidden by default until show() is called) */
  init(locale: string): void {
    if (!this.key || this.loaded) {
      return;
    }
    this.loaded = true;

    const script = document.createElement('script');
    script.id = 'ze-snippet';
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${this.key}`;
    script.async = true;

    script.onload = () => {
      this.doHide();
      this.setLocale(locale);
      this.applyThemeColors();
      this.resolveReady();
    };

    script.onerror = () => {
      this.loaded = false;
    };

    document.head.appendChild(script);
  }

  /** Pre-authenticate the current user with Zendesk (call once after login) */
  authenticate(): void {
    if (!this.key) return;
    this.readyPromise.then(() => this.loginUser());
  }

  /** Clear Zendesk session (call on sign-out) */
  logout(): void {
    if (window.zE) {
      window.zE('messenger', 'logoutUser');
      this.authenticated = false;
      this.resetAuthPromise();
    }
  }

  /** Show the Zendesk launcher (waits for authentication to complete) */
  show(): void {
    this.wantVisible = true;
    this.authPromise.then(() => {
      if (this.wantVisible) this.doShow();
    });
  }

  /** Open the conversation programmatically (for the viewer's custom button) */
  open(): void {
    this.authPromise.then(() => {
      if (window.zE) {
        this.doShow();
        window.zE('messenger', 'open');
        window.zE('messenger:on', 'close', () => {
          if (!this.wantVisible) this.doHide();
        });
      }
    });
  }

  /** Hide the Zendesk launcher */
  hide(): void {
    this.wantVisible = false;
    this.doHide();
  }

  /** Set the widget locale — falls back to English for unsupported languages (e.g. lb) */
  setLocale(locale: string): void {
    const supported = ['fr', 'en', 'de'];
    this.locale = supported.includes(locale) ? locale : 'en';
    if (window.zE) {
      window.zE('messenger:set', 'locale', this.locale);
    }
  }

  /** Update the Zendesk user profile locale server-side (call on language change) */
  updateServerLocale(locale: string): void {
    this.http.put(`${environment.apiUrl}/zendesk/locale`, { locale }).subscribe();
  }

  private doShow(): void {
    if (window.zE) {
      window.zE('messenger', 'show');
    }
  }

  private doHide(): void {
    if (window.zE) {
      window.zE('messenger', 'hide');
    }
  }

  /** Apply the app theme colors to the Zendesk widget (header, launcher, and message bubbles) */
  private applyThemeColors(): void {
    if (!window.zE) return;
    const style = getComputedStyle(document.documentElement);
    const primary = style.getPropertyValue('--main-color').trim();
    const primaryLight = style.getPropertyValue('--main-color-light').trim();
    if (primary) {
      window.zE('messenger:set', 'customization', {
        theme: {
          primary,
          message: primary,
          businessMessage: primaryLight || primary,
          action: primary
        }
      });
    }
  }

  private loginUser(): void {
    if (this.authenticated) {
      return;
    }
    this.authenticated = true;

    window.zE!('messenger', 'loginUser',
      (resolve: (jwt: string) => void) => {
        firstValueFrom(
          this.http.get<{ token: string }>(`${environment.apiUrl}/zendesk/messaging-token`)
        ).then(
          res => resolve(res.token),
          () => {
            this.authenticated = false;
            this.resolveAuth();
          }
        );
      },
      (err: any) => {
        // loginCallback: called by Zendesk when authentication is fully complete
        if (!err) {
          this.setLocale(this.locale);
          this.resolveAuth();
        } else {
          this.authenticated = false;
          this.resolveAuth();
        }
      }
    );
  }

  private resetAuthPromise(): void {
    this.authPromise = new Promise(resolve => this.resolveAuth = resolve);
  }
}
